"use client";

import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronUp } from "lucide-react";

export default function ScrollToTop() {
    const [isVisible, setIsVisible] = useState(false);
    const rafRef = useRef<number | null>(null);
    const lastKnownScroll = useRef(0);
    const ticking = useRef(false);

    // Set the scroll event listener with throttling via rAF
    useEffect(() => {
        const onScroll = () => {
            lastKnownScroll.current = window.scrollY;
            if (!ticking.current) {
                rafRef.current = requestAnimationFrame(() => {
                    setIsVisible(lastKnownScroll.current > 300);
                    ticking.current = false;
                });
                ticking.current = true;
            }
        };

        // Initialize visibility for pages that load already scrolled
        lastKnownScroll.current = window.scrollY;
        setIsVisible(lastKnownScroll.current > 300);

        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", onScroll);
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
            }
        };
    }, []);

    // Scroll to top smoothly
    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.button
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.5 }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={scrollToTop}
                    className="fixed bottom-6 ltr:right-6 rtl:left-6 z-50 p-3 rounded-full bg-gold text-white shadow-lg hover:shadow-xl transition-shadow duration-300 focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-background"
                    aria-label="Scroll to top"
                >
                    <ChevronUp className="w-6 h-6" />
                </motion.button>
            )}
        </AnimatePresence>
    );
} 
